var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Select, Store, Actions, ofActionDispatched } from '@ngxs/store';
import { OrganizationsState } from 'src/app/organizations/state/organizations.state';
import { Observable, Subject } from 'rxjs';
import { FormBuilder, Validators } from '@angular/forms';
import { SaveBookmark, SaveBookmarkSuccess, SaveBookmarkFail } from 'src/app/bookmarks/state/bookmarks.actions';
import { OrganizationsService } from 'src/app/organizations/organizations.service';
import { map, debounceTime } from 'rxjs/operators';
import { takeUntil } from 'rxjs/operators';
import { GetCollections } from 'src/app/organizations/state/organizations.actions';
import { BookmarksState } from 'src/app/bookmarks/state/bookmarks.state';
export var SaveBookmarkDialogMode;
(function (SaveBookmarkDialogMode) {
    SaveBookmarkDialogMode[SaveBookmarkDialogMode["SaveStory"] = 0] = "SaveStory";
    SaveBookmarkDialogMode[SaveBookmarkDialogMode["Manage"] = 1] = "Manage";
})(SaveBookmarkDialogMode || (SaveBookmarkDialogMode = {}));
export class SaveBookmarkDialogComponent {
    constructor(store, fb, dialogRef, data, snackBar, actions, organizationsService) {
        this.store = store;
        this.fb = fb;
        this.dialogRef = dialogRef;
        this.data = data;
        this.snackBar = snackBar;
        this.actions = actions;
        this.organizationsService = organizationsService;
        this.saveDebounce = new Subject();
        this.settings = {
            mode: SaveBookmarkDialogMode.SaveStory
        };
        this.ngUnsubscribe = new Subject();
        this.settings = Object.assign({}, this.settings, data);
        this.form = this.fb.group({
            title: ['', [Validators.required, Validators.maxLength(60)]],
            collection: [null],
            groupAccess: [null],
            publicAccess: [false]
        });
        // Listen for bookmark actions
        this.actions
            .pipe(ofActionDispatched(SaveBookmarkSuccess), takeUntil(this.ngUnsubscribe))
            .subscribe((response) => this.handleSaveSuccess(response));
        this.actions
            .pipe(ofActionDispatched(SaveBookmarkFail), takeUntil(this.ngUnsubscribe))
            .subscribe((response) => this.handleSaveFailure(response));
        this.saveDebounce
            .pipe(debounceTime(100), takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
            this.saveBookmark();
        });
    }
    get dialogTitle() {
        return this.settings.title || (this.settings.mode === SaveBookmarkDialogMode.Manage ? 'Manage' : 'Save');
    }
    ngOnInit() {
        // populate group collections
        const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
        if (organization) {
            this.store.dispatch(new GetCollections(organization.id));
        }
        // set form
        const bookmark = this.settings.bookmark;
        const title = bookmark ? bookmark.title : (this.settings.bookmarkTitle ? this.settings.bookmarkTitle : '');
        // group access state
        const groupAccess = bookmark ? this.getGroupAccess(bookmark) : null;
        let groupEdit = false;
        let groupView = false;
        if (groupAccess) { // set group access via data
            groupEdit = groupAccess.member_edit;
            groupView = !groupEdit && groupAccess.visible;
        }
        else if (!bookmark && organization) { // default group users
            groupEdit = false;
            groupView = true;
        }
        // public access: non-group users
        const publicAccess = !this.isGroupUser() && (bookmark && bookmark.tags.includes('public'));
        this.form.patchValue({
            title: title,
            groupAccess: groupEdit ? 'edit' : (groupView ? 'view' : ''),
            publicAccess: publicAccess
        });
        // set mode
        this.isNew = true;
        this.isOwner = true;
        if (bookmark) {
            if (bookmark.is_owner) {
                this.isNew = false;
                this.isOwner = true;
            }
            else {
                const isEditable = bookmark.group_access.some((access) => access.member_edit === true);
                this.isNew = !isEditable;
                this.isOwner = this.isNew;
            }
        }
        // setup group access
        this.initGroupAccess();
        // setup collections
        this.initCollections();
        // set group access
        this.toggleGroupAccess(groupView, groupEdit);
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    initGroupAccess() {
        const groupAccessField = this.form.get('groupAccess');
        // set collections: via group access
        groupAccessField.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((groupAccess) => {
            const groupView = groupAccess === 'view';
            const groupEdit = groupAccess === 'edit';
            this.toggleGroupAccess(groupView, groupEdit);
        });
    }
    initCollections() {
        const bookmark = this.settings.bookmark;
        const formCollection = this.form.get('collection');
        formCollection.disable();
        const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
        if (organization && bookmark) {
            const collection$ = this.organizationsService.getCollectionsByContent(organization.id, {
                content_type: bookmark.type,
                content_id: bookmark.id
            });
            collection$
                .pipe(map((contentCollections) => contentCollections[0]), takeUntil(this.ngUnsubscribe))
                .subscribe((contentCollection) => {
                this.currentCollection = contentCollection;
                const currentCollectionId = this.currentCollection ? this.currentCollection.collection_id : null;
                this.form.patchValue({
                    collection: currentCollectionId
                });
            });
        }
    }
    toggleGroupAccess(groupView, groupEdit) {
        const collection = this.form.get('collection');
        const canEditCollections = this.isOwner || this.isNew;
        const isGroupAccess = groupView || groupEdit;
        // clear selection
        if (!isGroupAccess) {
            collection.setValue(null);
        }
        // set enabled state   
        if (canEditCollections && isGroupAccess) {
            collection.enable();
        }
        else {
            collection.disable();
        }
    }
    handleSubmit() {
        if (this.form.valid) {
            this.saveDebounce.next();
        }
    }
    saveBookmark() {
        const bookmarkEntry = this.getBookmarkEntry();
        const includeIndex = this.settings.mode === SaveBookmarkDialogMode.SaveStory;
        this.store.dispatch(new SaveBookmark(bookmarkEntry, false, includeIndex));
    }
    handleSaveSuccess(response) {
        const bookmark = response.bookmark;
        const message = this.settings.mode === SaveBookmarkDialogMode.Manage ?
            `Your updates to "${bookmark.title}" have been saved to your dashboard.` :
            `"${bookmark.title}" saved to your dashboard.`;
        this.snackBar.open(message);
        this.dialogRef.close(bookmark);
    }
    handleSaveFailure(response) {
        this.snackBar.open(`Unable to save bookmark "${response.bookmarkEntry.title}". Please try again.`);
    }
    getBookmarkEntry() {
        const { bookmark } = this.settings;
        const { title, collection, groupAccess, publicAccess } = this.form.value;
        const groupView = groupAccess === 'view';
        const groupEdit = groupAccess === 'edit';
        const isPublicAccess = this.isGroupUser() ? null : publicAccess;
        let addGroupAccess = [];
        let removeGroupAccess = [];
        let addCollection;
        let removeCollection;
        // owner: manage permissions + collections
        if (this.isOwner) {
            const groupAccessPermission = this.createGroupAccess(groupView, groupEdit);
            if (groupAccessPermission) {
                // set group access if selected
                addGroupAccess = [groupAccessPermission];
            }
            else {
                // remove all existing access if none selected
                removeGroupAccess = bookmark ? bookmark.group_access : [];
            }
            // handle collections
            const collections = this.store.selectSnapshot(OrganizationsState.getCollections) || [];
            const selectedCollectionId = parseInt(collection, 10);
            const selectedCollection = collections.find((c) => c.collection_id === selectedCollectionId);
            if (this.currentCollection) { // update collection
                const isCurrentSelected = this.currentCollection.collection_id === selectedCollectionId;
                if (!isCurrentSelected) { // only update when changing
                    addCollection = selectedCollection;
                    removeCollection = this.currentCollection;
                }
            }
            else if (selectedCollection) { // add optional collection
                addCollection = selectedCollection;
            }
        }
        return {
            isOwner: bookmark ? bookmark.is_owner : true,
            id: bookmark ? bookmark.id : null,
            title: title,
            add_group_access: addGroupAccess,
            remove_group_access: removeGroupAccess,
            addCollection: addCollection,
            removeCollection: removeCollection,
            public_access: isPublicAccess
        };
    }
    getGroupAccess(bookmark) {
        const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
        let groupAccess = null;
        if (organization && bookmark.group_access) {
            const organizationId = organization.id;
            groupAccess = bookmark.group_access.find((bookmarkAccessLevel) => bookmarkAccessLevel.group_id === organizationId);
        }
        return groupAccess;
    }
    createGroupAccess(groupView, groupEdit) {
        const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
        const isValidAccess = groupView || groupEdit;
        if (organization && isValidAccess) {
            return {
                group_id: organization.id,
                member_edit: groupEdit,
                visible: groupView || groupEdit
            };
        }
        else {
            return null;
        }
    }
    isGroupUser() {
        return !!this.store.selectSnapshot(OrganizationsState.getOrganization);
    }
}
__decorate([
    Select(OrganizationsState.getOrganization),
    __metadata("design:type", Observable)
], SaveBookmarkDialogComponent.prototype, "organization$", void 0);
__decorate([
    Select(OrganizationsState.getCollections),
    __metadata("design:type", Observable)
], SaveBookmarkDialogComponent.prototype, "collections$", void 0);
__decorate([
    Select(BookmarksState.getSaving),
    __metadata("design:type", Observable)
], SaveBookmarkDialogComponent.prototype, "saving$", void 0);
